import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI } from '../../services/analyticsAPI';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Building,
  Target,
  Download,
  RefreshCw
} from 'lucide-react';

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [filteredData, setFilteredData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiInsightsLoading, setAiInsightsLoading] = useState(false);
  const [filters, setFilters] = useState({
    years: [new Date().getFullYear()], // Changed to array for multi-select
    sites: [], // Array for multi-select
    pillar: 'all',
    timeRange: '30d'
  });

  // Helper function for role-aware navigation
  const navigateToRoleDashboard = () => {
    const isLeader = window.location.pathname.includes('/leaders/');
    const dashboardPath = isLeader ? '/gps/leaders/dashboard' : '/gps/team/dashboard';
    navigate(dashboardPath);
  };

  useEffect(() => {
    loadAnalyticsData();
  }, []);

  useEffect(() => {
    if (analyticsData) {
      applyFiltersToData();
    }
  }, [filters, analyticsData]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      
      // Try to load real data from the analytics API
      try {
        console.log('🔄 Loading analytics data from API...');
        const data = await analyticsAPI.getDashboardData(filters);
        console.log('✅ Analytics data loaded successfully:', data);
        
        // Ensure data has the expected structure
        const processedData = {
          kpis: data.kpis || {
            totalSites: 0,
            activeAssessments: 0,
            overallCompletion: 0,
            monthlyResponses: 0,
            topPillar: { name: 'No data', score: 0 },
            attentionNeeded: { site: 'No data', completion: 0 }
          },
          sitePerformance: data.sitePerformance || [],
          pillarPerformance: data.pillarPerformance || [],
          trends: data.trends || { completionTrend: [] },
          lastUpdated: data.lastUpdated || new Date().toISOString()
        };
        
        setAnalyticsData(processedData);
        return;
        
      } catch (apiError) {
        console.warn('⚠️ Analytics API not available, using fallback data:', apiError.message);
        
        // Fallback to mock data if API is not available
        const mockData = {
          kpis: {
            totalSites: 25,
            activeAssessments: 156,
            overallCompletion: 78,
            monthlyResponses: 2847,
            topPillar: { name: 'We Drive Flow', score: 89 },
            attentionNeeded: { site: 'Site Gamma', completion: 45 }
          },
          sitePerformance: [
            { name: 'Site Alpha', completion: 92, avgScore: 4.2, trend: 'up', lastUpdated: '2 hours ago' },
            { name: 'Site Beta', completion: 78, avgScore: 3.8, trend: 'down', lastUpdated: '1 day ago' },
            { name: 'Site Gamma', completion: 45, avgScore: 3.1, trend: 'up', lastUpdated: '3 hours ago' },
            { name: 'Site Delta', completion: 85, avgScore: 4.0, trend: 'stable', lastUpdated: '5 hours ago' }
          ],
          pillarPerformance: [
            { name: 'We Drive Flow', completion: 89, responses: 1250, avgScore: 4.1 },
            { name: 'We Develop People', completion: 82, responses: 1100, avgScore: 3.9 },
            { name: 'We Improve Everything', completion: 75, responses: 980, avgScore: 3.7 },
            { name: 'We Respect Everyone', completion: 88, responses: 1180, avgScore: 4.0 },
            { name: 'We Deliver Results', completion: 71, responses: 890, avgScore: 3.6 }
          ],
          trends: {
            completionTrend: [
              { month: 'Jan', completion: 65 },
              { month: 'Feb', completion: 70 },
              { month: 'Mar', completion: 75 },
              { month: 'Apr', completion: 78 }
            ]
          },
          lastUpdated: new Date().toISOString(),
          isTestData: true
        };
        
        setAnalyticsData(mockData);
        setFilteredData(mockData);
      }
      
    } catch (error) {
      console.error('❌ Failed to load analytics data:', error);
      
      // Set empty data structure on error
      const emptyData = {
        kpis: {
          totalSites: 0,
          activeAssessments: 0,
          overallCompletion: 0,
          monthlyResponses: 0,
          topPillar: { name: 'No data available', score: 0 },
          attentionNeeded: { site: 'No data available', completion: 0 }
        },
        sitePerformance: [],
        pillarPerformance: [],
        trends: { completionTrend: [] },
        lastUpdated: new Date().toISOString(),
        hasError: true
      };
      
      setAnalyticsData(emptyData);
      setFilteredData(emptyData);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersToData = async () => {
    if (!analyticsData) return;

    // Apply filters to the data
    let filtered = { ...analyticsData };

    // Filter site performance (multi-select)
    if (filters.sites.length > 0) {
      filtered.sitePerformance = analyticsData.sitePerformance.filter(
        site => filters.sites.some(selectedSite => 
          site.name.toLowerCase().includes(selectedSite.toLowerCase())
        )
      );
    }

    // Filter pillar performance based on selected sites
    if (filters.sites.length > 0) {
      // When sites are filtered, adjust pillar performance to reflect only those sites
      filtered.pillarPerformance = analyticsData.pillarPerformance.map(pillar => ({
        ...pillar,
        // Simulate filtering pillar data based on selected sites
        // In real implementation, this would aggregate data from selected sites only
        responses: Math.round(pillar.responses * (filtered.sitePerformance.length / analyticsData.sitePerformance.length)),
        completion: Math.round(pillar.completion + (Math.random() - 0.5) * 10), // Round to whole number
        avgScore: Math.round((Math.max(1, Math.min(5, pillar.avgScore + (Math.random() - 0.5) * 0.5))) * 10) / 10 // Round to 1 decimal
      }));
    }

    // Filter pillar performance by pillar type
    if (filters.pillar !== 'all') {
      filtered.pillarPerformance = filtered.pillarPerformance.filter(
        pillar => pillar.name.toLowerCase().includes(filters.pillar.toLowerCase())
      );
    }

    // Apply year filtering (simulate multi-year trend analysis)
    if (filters.years.length > 0 && filters.years.length < 5) {
      // Simulate year-over-year data variation
      const yearMultiplier = filters.years.length / 5; // Adjust data based on years selected
      filtered.pillarPerformance = filtered.pillarPerformance.map(pillar => ({
        ...pillar,
        responses: Math.round(pillar.responses * yearMultiplier),
        // Simulate year-over-year trends
        completion: Math.round(pillar.completion + (filters.years.includes(2024) ? 5 : 0) + (filters.years.includes(2023) ? 2 : 0)),
        avgScore: Math.round((pillar.avgScore + (filters.years.length * 0.1)) * 10) / 10
      }));
    }

    // Recalculate KPIs based on filtered data
    filtered.kpis = calculateFilteredKPIs(filtered);

    setFilteredData(filtered);

    // Generate AI insights based on filtered data
    await generateAIInsights(filtered);
  };

  const calculateFilteredKPIs = (data) => {
    const totalSites = data.sitePerformance.length;
    const avgCompletion = Math.round(
      data.sitePerformance.reduce((sum, site) => sum + site.completion, 0) / totalSites
    );
    const totalResponses = data.pillarPerformance.reduce((sum, pillar) => sum + pillar.responses, 0);
    
    const topPillar = data.pillarPerformance.reduce((top, pillar) => 
      pillar.completion > top.completion ? pillar : top
    );

    const attentionSite = data.sitePerformance.reduce((lowest, site) => 
      site.completion < lowest.completion ? site : lowest
    );

    return {
      totalSites,
      activeAssessments: Math.round(totalSites * 6.24), // Estimated based on sites
      overallCompletion: avgCompletion,
      monthlyResponses: totalResponses,
      topPillar: { name: topPillar.name, score: topPillar.completion },
      attentionNeeded: { site: attentionSite.name, completion: attentionSite.completion }
    };
  };

  const generateAIInsights = async (data) => {
    try {
      setAiInsightsLoading(true);
      
      // TODO: Replace with actual Bedrock API call
      // const aiInsights = await analyticsAPI.generateAIInsights(data, filters);
      
      // Mock AI insights for now
      const mockAIInsights = [
        {
          type: 'trend',
          title: 'Year-over-Year Trend Analysis',
          message: filters.years.length > 1 
            ? `Multi-year analysis shows ${filters.years.includes(2024) && filters.years.includes(2023) ? 'consistent improvement' : 'mixed performance'} across ${filters.years.join(', ')} with ${data.sitePerformance.length} sites averaging ${data.kpis.overallCompletion}% completion.`
            : `${filters.years[0] || 'Current year'} performance shows ${Math.random() > 0.5 ? 'positive' : 'stable'} trends with ${data.sitePerformance.length} sites averaging ${data.kpis.overallCompletion}% completion.`,
          confidence: 0.87
        },
        {
          type: 'recommendation',
          title: 'AI Recommendation',
          message: filters.years.length > 1
            ? `Year-over-year comparison reveals ${data.sitePerformance.filter(s => s.completion < 70).length} sites need attention. Historical data suggests focusing on consistency could improve overall performance by 15-20%.`
            : `Focus improvement efforts on ${data.sitePerformance.filter(s => s.completion < 70).length} underperforming sites. Implementing best practices from top performers could increase overall completion by 12-18%.`,
          confidence: 0.92
        },
        {
          type: 'prediction',
          title: 'Predictive Insight',
          message: filters.years.length > 1
            ? `Multi-year trend analysis suggests ${data.kpis.overallCompletion > 75 ? 'continued growth trajectory' : 'stabilization needed'} with projected ${Math.round(data.kpis.overallCompletion * 1.1)}% completion by next year.`
            : `Current trajectory suggests ${filters.timeRange === '30d' ? 'monthly' : 'quarterly'} targets will be ${data.kpis.overallCompletion > 75 ? 'exceeded by 8-12%' : 'missed by 5-10%'} if current patterns continue.`,
          confidence: 0.79
        }
      ];

      // Update filtered data with AI insights
      setFilteredData(prev => ({
        ...prev,
        aiInsights: mockAIInsights
      }));

    } catch (error) {
      console.error('Failed to generate AI insights:', error);
    } finally {
      setAiInsightsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="assessment-details-container">
        <div className="assessment-header">
          <div className="container">
            <button className="assessment-back-button" onClick={navigateToRoleDashboard}>
              ← Back to Dashboard
            </button>
          </div>
        </div>
        <div className="loading-container">
          <div className="spinner"></div>
          <p className="loading-text">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-details-container">
      {/* Header */}
      <div className="assessment-header">
        <div className="container">
          <button className="assessment-back-button" onClick={navigateToRoleDashboard}>
            ← Back to Dashboard
          </button>
          
          <h1 className="assessment-title">
            Analytics & Reporting
          </h1>
          
          <p className="assessment-subtitle">
            Enterprise insights and performance analytics
          </p>
        </div>
      </div>

      <div className="assessment-content">
        {/* Filter Bar */}
        <div className="analytics-filter-bar">
          <div className="filter-controls">
            <div className="multi-select-container">
              <label className="filter-label">Years:</label>
              <YearMultiSelect 
                availableYears={[2024, 2023, 2022, 2021, 2020]}
                selectedYears={filters.years}
                onChange={(years) => setFilters(prev => ({ ...prev, years }))}
              />
            </div>
            
            <div className="multi-select-container">
              <label className="filter-label">Sites:</label>
              <SiteMultiSelect 
                availableSites={['Site Alpha', 'Site Beta', 'Site Gamma', 'Site Delta']}
                selectedSites={filters.sites}
                onChange={(sites) => setFilters(prev => ({ ...prev, sites }))}
              />
            </div>
            
            <select
              value={filters.pillar}
              onChange={(e) => setFilters(prev => ({ ...prev, pillar: e.target.value }))}
              className="form-input"
            >
              <option value="all">All Pillars</option>
              <option value="drive flow">We Drive Flow</option>
              <option value="develop people">We Develop People</option>
              <option value="improve everything">We Improve Everything</option>
              <option value="respect everyone">We Respect Everyone</option>
              <option value="deliver results">We Deliver Results</option>
            </select>
            
            <select
              value={filters.timeRange}
              onChange={(e) => setFilters(prev => ({ ...prev, timeRange: e.target.value }))}
              className="form-input"
            >
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="1y">Last Year</option>
            </select>
          </div>
          
          <div className="filter-actions">
            <button className="questionnaire-button questionnaire-button-secondary" onClick={loadAnalyticsData}>
              <RefreshCw size={16} />
              Refresh
            </button>
            <button className="questionnaire-button questionnaire-button-primary">
              <Download size={16} />
              Export
            </button>
          </div>
        </div>

        {/* KPI Cards - Dynamic based on filters */}
        <div className="stats-grid">
          <KPICard
            title="Filtered Sites"
            value={filteredData.kpis.totalSites}
            icon={Building}
            color="primary"
            subtitle={filters.sites.length > 0 ? `${filters.sites.length} selected` : 'All sites'}
          />
          <KPICard
            title="Active Assessments"
            value={filteredData.kpis.activeAssessments}
            icon={BarChart3}
            color="success"
            subtitle={filters.years.length === 1 ? `${filters.years[0]} assessments` : `${filters.years.length} years selected`}
          />
          <KPICard
            title="Avg Completion"
            value={`${filteredData.kpis.overallCompletion}%`}
            icon={Target}
            color="accent"
            subtitle={`Last ${filters.timeRange}`}
          />
          <KPICard
            title="Total Responses"
            value={filteredData.kpis.monthlyResponses.toLocaleString()}
            icon={Users}
            color="info"
            subtitle="Filtered dataset"
          />
        </div>

        {/* Site Performance Table - Filtered */}
        <div className="questionnaires-section">
          <div className="questionnaires-header">
            <h2 className="questionnaires-title">
              Site Performance Overview
              <div className="filter-indicators">
                {filters.years.length > 0 && filters.years.length < 5 && (
                  <span className="filter-indicator">
                    Years: {filters.years.length === 1 ? filters.years[0] : `${filters.years.join(', ')}`}
                  </span>
                )}
                {filters.sites.length > 0 && (
                  <span className="filter-indicator">
                    Sites: {filters.sites.length === 1 ? filters.sites[0] : `${filters.sites.length} selected`}
                  </span>
                )}
              </div>
            </h2>
          </div>
          <div className="questionnaires-content">
            <SitePerformanceTable sites={filteredData.sitePerformance} />
          </div>
        </div>

        {/* Pillar Performance - Filtered */}
        <div className="questionnaires-section">
          <div className="questionnaires-header">
            <h2 className="questionnaires-title">
              Pillar Performance Analysis
              <div className="filter-indicators">
                {filters.years.length > 0 && filters.years.length < 5 && (
                  <span className="filter-indicator">
                    Years: {filters.years.length === 1 ? filters.years[0] : `${filters.years.join(', ')}`}
                  </span>
                )}
                {filters.sites.length > 0 && (
                  <span className="filter-indicator">
                    Sites: {filters.sites.length === 1 ? filters.sites[0] : `${filters.sites.length} selected`}
                  </span>
                )}
                {filters.pillar !== 'all' && (
                  <span className="filter-indicator">Pillar: {filters.pillar}</span>
                )}
              </div>
            </h2>
          </div>
          <div className="questionnaires-content">
            <PillarPerformanceGrid pillars={filteredData.pillarPerformance} />
          </div>
        </div>

        {/* AI-Enhanced Insights */}
        <div className="questionnaires-section">
          <div className="questionnaires-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 className="questionnaires-title">AI-Powered Insights</h2>
              {aiInsightsLoading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div className="spinner" style={{ width: '1rem', height: '1rem' }}></div>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    Generating insights...
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="questionnaires-content">
            <EnhancedInsightsPanel 
              data={filteredData} 
              filters={filters}
              loading={aiInsightsLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const YearMultiSelect = ({ availableYears, selectedYears, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleYear = (year) => {
    const newSelection = selectedYears.includes(year)
      ? selectedYears.filter(y => y !== year)
      : [...selectedYears, year].sort((a, b) => b - a); // Sort descending (newest first)
    onChange(newSelection);
  };

  const clearAll = () => {
    onChange([]);
  };

  const selectAll = () => {
    onChange([...availableYears]);
  };

  return (
    <div className="multi-select-wrapper" ref={dropdownRef}>
      <button
        className="multi-select-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>
          {selectedYears.length === 0 
            ? 'All Years' 
            : selectedYears.length === 1 
            ? selectedYears[0]
            : `${selectedYears.length} Years Selected`
          }
        </span>
        <span className={`multi-select-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </button>
      
      {isOpen && (
        <div className="multi-select-dropdown">
          <div className="multi-select-actions">
            <button onClick={selectAll} className="multi-select-action">Select All</button>
            <button onClick={clearAll} className="multi-select-action">Clear All</button>
          </div>
          
          {availableYears.map(year => (
            <label key={year} className="multi-select-option">
              <input
                type="checkbox"
                checked={selectedYears.includes(year)}
                onChange={() => toggleYear(year)}
              />
              <span className="multi-select-checkbox"></span>
              <span className="multi-select-label">{year}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const SiteMultiSelect = ({ availableSites, selectedSites, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleSite = (site) => {
    const newSelection = selectedSites.includes(site)
      ? selectedSites.filter(s => s !== site)
      : [...selectedSites, site];
    onChange(newSelection);
  };

  const clearAll = () => {
    onChange([]);
  };

  const selectAll = () => {
    onChange([...availableSites]);
  };

  return (
    <div className="multi-select-wrapper" ref={dropdownRef}>
      <button
        className="multi-select-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>
          {selectedSites.length === 0 
            ? 'All Sites' 
            : selectedSites.length === 1 
            ? selectedSites[0]
            : `${selectedSites.length} Sites Selected`
          }
        </span>
        <span className={`multi-select-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </button>
      
      {isOpen && (
        <div className="multi-select-dropdown">
          <div className="multi-select-actions">
            <button onClick={selectAll} className="multi-select-action">Select All</button>
            <button onClick={clearAll} className="multi-select-action">Clear All</button>
          </div>
          
          {availableSites.map(site => (
            <label key={site} className="multi-select-option">
              <input
                type="checkbox"
                checked={selectedSites.includes(site)}
                onChange={() => toggleSite(site)}
              />
              <span className="multi-select-checkbox"></span>
              <span className="multi-select-label">{site}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const KPICard = ({ title, value, icon: Icon, color, subtitle }) => {
  const colorClasses = {
    primary: { bg: 'var(--primary-100)', text: 'var(--primary-600)' },
    success: { bg: 'var(--success-100)', text: 'var(--success-600)' },
    accent: { bg: 'var(--accent-100)', text: 'var(--accent-600)' },
    info: { bg: '#e0e7ff', text: '#3730a3' }
  };

  const colors = colorClasses[color] || colorClasses.primary;

  return (
    <div className="stats-card">
      <div className="stats-card-icon" style={{ background: colors.bg, color: colors.text }}>
        <Icon size={24} />
      </div>
      <div className="stats-card-value">{value}</div>
      <div className="stats-card-label">{title}</div>
      {subtitle && (
        <div className="stats-card-subtitle">{subtitle}</div>
      )}
    </div>
  );
};

const SitePerformanceTable = ({ sites }) => {
  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <TrendingUp size={16} style={{ color: 'var(--success-600)' }} />;
      case 'down': return <TrendingUp size={16} style={{ color: 'var(--error-600)', transform: 'rotate(180deg)' }} />;
      default: return <span style={{ color: 'var(--neutral-500)' }}>—</span>;
    }
  };

  return (
    <div className="analytics-table">
      <table>
        <thead>
          <tr>
            <th>Site Name</th>
            <th>Completion %</th>
            <th>Avg Score</th>
            <th>Trend</th>
            <th>Last Updated</th>
          </tr>
        </thead>
        <tbody>
          {sites.map((site, index) => (
            <tr key={index}>
              <td className="font-semibold">{site.name}</td>
              <td>
                <span className={`completion-badge ${site.completion >= 80 ? 'high' : site.completion >= 60 ? 'medium' : 'low'}`}>
                  {site.completion}%
                </span>
              </td>
              <td>{site.avgScore}/5</td>
              <td>{getTrendIcon(site.trend)}</td>
              <td className="text-muted">{site.lastUpdated}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PillarPerformanceGrid = ({ pillars }) => {
  return (
    <div className="pillar-grid">
      {pillars.map((pillar, index) => (
        <div key={index} className="pillar-card">
          <h3 className="pillar-name">{pillar.name}</h3>
          <div className="pillar-metrics">
            <div className="metric">
              <span className="metric-value">{pillar.completion}%</span>
              <span className="metric-label">Completion</span>
            </div>
            <div className="metric">
              <span className="metric-value">{pillar.responses}</span>
              <span className="metric-label">Responses</span>
            </div>
            <div className="metric">
              <span className="metric-value">{pillar.avgScore}/5</span>
              <span className="metric-label">Avg Score</span>
            </div>
          </div>
          <div className="pillar-progress">
            <div 
              className="progress-bar"
              style={{ width: `${pillar.completion}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const EnhancedInsightsPanel = ({ data, filters, loading }) => {
  // Static insights based on filtered data
  const staticInsights = [
    {
      type: 'success',
      title: 'Top Performer',
      message: `${data.kpis.topPillar.name} is leading with ${data.kpis.topPillar.score}% completion rate`,
      source: 'data'
    },
    {
      type: 'warning',
      title: 'Attention Needed',
      message: `${data.kpis.attentionNeeded.site} needs support with only ${data.kpis.attentionNeeded.completion}% completion`,
      source: 'data'
    }
  ];

  // Combine static and AI insights
  const allInsights = [
    ...staticInsights,
    ...(data.aiInsights || [])
  ];

  return (
    <div className="insights-grid">
      {allInsights.map((insight, index) => (
        <div key={index} className={`insight-card ${insight.type}`}>
          <div className="insight-header">
            <h4 className="insight-title">{insight.title}</h4>
            {insight.source !== 'data' && (
              <div className="ai-badge">
                <span>🤖 AI</span>
                {insight.confidence && (
                  <span className="confidence-score">
                    {Math.round(insight.confidence * 100)}%
                  </span>
                )}
              </div>
            )}
          </div>
          <p className="insight-message">{insight.message}</p>
        </div>
      ))}
      
      {loading && (
        <div className="insight-card loading">
          <div className="insight-header">
            <h4 className="insight-title">Generating AI Insights...</h4>
            <div className="spinner" style={{ width: '1rem', height: '1rem' }}></div>
          </div>
          <p className="insight-message">
            Analyzing filtered data to provide personalized recommendations...
          </p>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;